import { Controller } from 'stimulus'
import { truncate } from '@helpers'

let activePasteController = null
let pasteListenerAttached = false

const PREVIEWABLE_IMAGE = /^image\/(jpe?g|png|gif|bmp|webp)$/i

const FILE_ICONS = [
  [/\.pdf$/i, 'fa-file-pdf', 'text-red-500'],
  [/\.(xls|xlsx|xlsm)$/i, 'fa-file-excel', 'text-green-600'],
  [/\.(doc|docx|odt)$/i, 'fa-file-word', 'text-blue-600'],
  [/\.csv$/i, 'fa-file-csv', 'text-green-600'],
  [/\.(zip|zipx)$/i, 'fa-file-archive', 'text-yellow-600'],
  [/\.(mov|mp4|avi|mpeg|mpg|wmv|flv)$/i, 'fa-file-video', 'text-purple-600'],
  [/\.(mp3|wav|wma)$/i, 'fa-file-audio', 'text-purple-600'],
  [/\.(heic|heif|bmp|gif|jpg|jpeg|png)$/i, 'fa-file-image', 'text-gray-500']
]

function fileIcon(name) {
  const match = FILE_ICONS.find(([pattern]) => pattern.test(name))
  return match ? [match[1], match[2]] : ['fa-file-alt', 'text-gray-500']
}

function formatSize(bytes) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

function getPastedFiles(event) {
  const cd = event.originalEvent?.clipboardData || event.clipboardData
  if (!cd) return []
  if (cd.files && cd.files.length) return Array.from(cd.files)
  const files = []
  if (cd.items) {
    for (let i = 0; i < cd.items.length; i += 1) {
      const item = cd.items[i]
      if (item.kind === 'file') {
        const f = item.getAsFile()
        if (f) files.push(f)
      }
    }
  }
  return files
}

function ensureGlobalPasteListener() {
  if (pasteListenerAttached) return
  pasteListenerAttached = true
  $(document).on('paste.fileUploaderPaste', (e) => {
    if (!activePasteController) return
    if ($(e.target).is('input, textarea, [contenteditable="true"]')) return
    const files = getPastedFiles(e)
    if (!files.length) return
    e.preventDefault()
    $(activePasteController.element).fileupload('add', { files })
  })
}

export default class extends Controller {
  initialize() {
    const { pbId, tilesId } = this.element.dataset
    // A view opts into preview tiles by rendering a container and pointing
    // data-tiles-id at it; everywhere else keeps the progress bars.
    this.tilesContainer = tilesId ? document.getElementById(tilesId) : null
    this.defaultProgressContainer = document.getElementById(pbId)
      || this.element.closest('.attachments-container')?.querySelector('.attachments-progress')
      || document.getElementById('progress-bar-container')
  }

  connect() {
    this.doneFileCounter = 0
    this.failedFileCounter = 0
    this.startedFileCounter = 0
    this.objectUrls = []

    this.$progressContainer = $(this.defaultProgressContainer)
    this.$tilesContainer = $(this.tilesContainer)

    const $el = $(this.element)
    const $dropZone = $el.closest('.attachments-container, form')
    const $effectiveDropZone = $dropZone.length ? $dropZone : $el
    const isCellZone = $dropZone.length > 0 && $dropZone.is('.attachments-container')
    // A visible .attachments-dropzone box gets the highlight and paste hover,
    // but drops anywhere in the surrounding zone still upload.
    const $dropzoneBox = $el.closest('.attachments-dropzone')

    this.setupDragHighlight($effectiveDropZone, $dropzoneBox.length ? $dropzoneBox : $effectiveDropZone)
    if (isCellZone) {
      this.setupPasteHover($effectiveDropZone)
    } else if ($dropzoneBox.length) {
      this.setupPasteHover($dropzoneBox)
    }

    $el.fileupload({
      replaceFileInput: false,
      dropZone: $effectiveDropZone,
      pasteZone: null,

      start: (_e) => {
        this.disableForm()
      },

      add: (_e, data) => {
        if (!this.validateFile(data.files[0])) return

        this.startedFileCounter += 1

        if (this.tilesMode) {
          data.tile = this.$tile(data.files[0])
          data.tile.appendTo(this.$tilesContainer)
        } else {
          data.progressBar = this.$progressBar(data.files[0].name)
          data.progressBar.appendTo(this.$progressContainer)
        }

        this.presignFile(data)
      },

      progress: (e, data) => {
        const pct = Math.floor(data.loaded / data.total * 100)
        if (this.tilesMode) {
          data.tile.find('.tile-pct').text(`${pct}%`)
          data.tile.find('.tile-fill').css('width', `${pct}%`)
        } else {
          data.progressBar.find('div').css('width', `${pct}%`).html(`${pct}%`)
        }
      },

      done: (_e, data) => {
        this.doneFileCounter += 1

        console.log('[file-uploader] done - createRecord:', this.$element.data('createRecord'), 'directUpload:', this.$element.data('directUpload'))

        if (this.$element.data('createRecord') === true) {
          this.createAttachmentRecord(data, this.doneFileCounter === this.startedFileCounter)
        }
        if (this.$element.data('directUpload') === true) {
          this.uploadFile(data)
          if (this.tilesMode) this.finishTile(data, null)
        } else {
          const $input = this.appendFileField(data)
          if (this.tilesMode) this.finishTile(data, $input)
        }
      },
      fail: (_e, data) => this.uploadFailed(data),
      always: () => this.settleIfDone()
    })
  }

  disconnect() {
    if ($(this.element).length) {
      $(this.element).fileupload('destroy')
    }
    this.objectUrls.forEach(url => URL.revokeObjectURL(url))
    this.objectUrls = []
    if (this.$dragHighlightZone) {
      this.$dragHighlightZone.off('.fileUploaderHighlight')
      this.$dragHighlightTarget.removeClass('is-dragover')
      this.$dragHighlightZone = null
      this.$dragHighlightTarget = null
    }
    if (this.$pasteHoverZone) {
      this.$pasteHoverZone.off('.fileUploaderPaste')
      this.$pasteHoverZone.removeClass('is-paste-hover')
      if (activePasteController === this) activePasteController = null
      this.$pasteHoverZone = null
    }
  }

  setupPasteHover($zone) {
    this.$pasteHoverZone = $zone
    ensureGlobalPasteListener()

    $zone.on('mouseenter.fileUploaderPaste', () => {
      activePasteController = this
      $zone.addClass('is-paste-hover')
    })
    $zone.on('mouseleave.fileUploaderPaste', () => {
      if (activePasteController === this) activePasteController = null
      $zone.removeClass('is-paste-hover')
    })
  }

  setupDragHighlight($zone, $target = $zone) {
    this.$dragHighlightZone = $zone
    this.$dragHighlightTarget = $target
    let dragCounter = 0
    const clear = () => {
      dragCounter = 0
      $target.removeClass('is-dragover')
    }

    $zone.on('dragenter.fileUploaderHighlight', (e) => {
      if (!Array.from(e.originalEvent.dataTransfer?.types || []).includes('Files')) return
      dragCounter += 1
      $target.addClass('is-dragover')
    })
    $zone.on('dragleave.fileUploaderHighlight', () => {
      dragCounter -= 1
      if (dragCounter <= 0) clear()
    })
    $zone.on('drop.fileUploaderHighlight', clear)
  }

  appendFileField(fileData) {
    // To append files, the data-name attribute of this.element needs to replace
    // the ordinal for models with an uploader attribute to "fileCounter"
    // Example: task_field[attachments_attributes][fileCounter][document]
    const name = this.$element.data('name').replace('[fileCounter]', this.doneFileCounter)
    const fileField = $(`<input type="hidden" name="${name}">`)
    const attachmentData = this.parseAttachmentData(fileData)

    fileField.val(attachmentData)
    const form = this.$form
    console.log('[file-uploader] appendFileField - name:', name, 'form found:', form.length > 0, 'form action:', form.attr('action'))
    form.append(fileField)
    console.log('[file-uploader] hidden fields in form:', form.find('input[type="hidden"]').length)
    return fileField
  }

  get tilesMode() {
    return this.$tilesContainer.length > 0
  }

  $tile(file) {
    const tile = $('<div class="attachment-tile uploading"></div>').attr('title', file.name)
    const thumb = $('<div class="tile-thumb"></div>')

    if (PREVIEWABLE_IMAGE.test(file.type)) {
      const url = URL.createObjectURL(file)
      this.objectUrls.push(url)
      tile.data('objectUrl', url)
      thumb.append($('<img alt="">').attr('src', url))
    } else {
      const [icon, color] = fileIcon(file.name)
      thumb.addClass('tile-doc').append(`<i class="fa ${icon} fa-2x ${color}"></i>`)
    }

    thumb.append('<span class="tile-pct">0%</span><div class="tile-track"><div class="tile-fill" style="width:0%"></div></div>')

    const caption = $('<div class="tile-caption"></div>')
      .append($('<div class="tile-name"></div>').text(file.name))
      .append($('<div class="tile-size"></div>').text(formatSize(file.size)))

    return tile.append(thumb, caption)
  }

  finishTile(data, $input) {
    const tile = data.tile
    tile.removeClass('uploading')
    tile.find('.tile-pct, .tile-track').remove()

    const remove = $('<button type="button" class="tile-remove">&times;</button>')
      .attr('aria-label', `Remove ${data.files[0].name}`)
    remove.on('click', () => {
      if ($input) $input.remove()
      const url = tile.data('objectUrl')
      if (url) URL.revokeObjectURL(url)
      tile.remove()
    })
    tile.find('.tile-thumb').append(remove)
  }

  errorTile(data) {
    const tile = data.tile
    tile.addClass('is-error').removeClass('uploading')

    const url = tile.data('objectUrl')
    if (url) {
      URL.revokeObjectURL(url)
      tile.removeData('objectUrl')
    }
    tile.find('.tile-thumb').removeClass('tile-doc').empty().append('<div class="tile-bang">!</div>')

    const retry = $('<a href="#" class="tile-retry">Retry</a>')
    retry.on('click', (e) => {
      e.preventDefault()
      this.retryUpload(data)
    })
    tile.find('.tile-caption').empty()
      .append('<div class="tile-error-text">Upload failed</div>')
      .append(retry)
  }

  retryUpload(data) {
    this.failedFileCounter -= 1
    const fresh = this.$tile(data.files[0])
    data.tile.replaceWith(fresh)
    data.tile = fresh
    this.disableForm()
    this.presignFile(data)
  }

  uploadFailed(data) {
    this.failedFileCounter += 1
    if (this.tilesMode && data.tile) this.errorTile(data)
    this.settleIfDone()
  }

  settleIfDone() {
    if (this.doneFileCounter + this.failedFileCounter === this.startedFileCounter) {
      this.enableForm()
    }
  }

  uploadFile(fileData) {
    const formData = new FormData(this.$form[0])
    const attachmentData = this.parseAttachmentData(fileData)

    formData.append(this.$element.data('name'), attachmentData)

    $.ajax(this.$form.attr('action'), {
      contentType: false,
      processData: false,
      data: formData,
      method: this.$form.attr('method'),
      dataType: 'script'
    })
  }

  createAttachmentRecord(fileData, isFinal) {
    const attachmentData = this.parseAttachmentData(fileData)
    const { dataset } = this.$element[0]
    let url
    let payload

    if (dataset.model === 'sample_trait') {
      payload = {
        sample_trait: {
          conditional_field_value_attributes: {
            id: dataset.id,
            attachments_attributes: [
              { document: attachmentData }
            ]
          }
        },
        authenticity_token: document.querySelector('meta[name="csrf-token"]').content,
        broadcast: isFinal ? 'true' : 'false',
        column_name: dataset.columnName || 'attachments',
        item_id: dataset.itemId
      }
      url = dataset.uploadUrl
    } else {
      payload = {
        sample_result_field_value: {
          id: dataset.id,
          attachments_attributes: [{ document: attachmentData }]
        },
        authenticity_token: document.querySelector('meta[name="csrf-token"]').content,
        broadcast: isFinal ? 'true' : 'false',
        column_name: 'attachments'
      }
      url = `/jobs/${dataset.jobId}/v2_sample_result_field_values/${dataset.id}`
    }

    $.ajax({
      url,
      method: 'PATCH',
      data: payload,
      error: xhr => alert(`Save failed: ${xhr.responseText}`)
    })
  }

  presignFile(fileData) {
    const options = {
      filename: fileData.files[0].name,
      _: Date.now()
    }

    $.getJSON('/presign', options, (result) => {
      fileData.formData = result.fields
      fileData.url = result.url
      fileData.paramName = 'file'
      fileData.submit()
    }).fail(() => this.uploadFailed(fileData))
  }

  parseAttachmentData(fileData) {
    const file = fileData.files[0]

    const attachmentData = {
      id: fileData.formData.key.match(/cache\/(.+)/)[1], // we have to remove the prefix part
      storage: 'cache',
      metadata: {
        size: file.size,
        filename: file.name.match(/[^/\\]+$/)[0], // IE return full path
        mime_type: file.type
      }
    }

    return JSON.stringify(attachmentData)
  }

  validateFile(file) {
    return this.validateFileSize(file)
  }

  validateFileType(file) {
    const types = /(\.|\/)(avi|bmp|csv|dad|dar|ddr|dds|doc|docx|dwg|flv|gif|jpg|jpeg|mov|mp3|mp4|mpeg|mpg|msg|odt|pdf|png|ppt|pptx|txt|wav|wma|wmv|xls|xlsm|xlsx|zip|zipx)$/i
    let response = true

    if (!types.test(file.type) && !types.test(file.name)) {
      alert(`${file.name} must be one of the following file types: avi, bmp, csv, doc, docx, dwg, flv, gif, jpg, jpeg, mov, mp3, mp4, mpeg, mpg, msg, pdf, ppt, pptx, txt, wav, wma, wmv, xls, xlsm, xlsx, zip, zipx`)
      response = false
    }

    return response
  }

  validateFileSize(file) {
    let response = true

    if (!(file.size < 5000 * 1024 * 1024)) {
      alert(`${file.name} is too large (max is 500 MB)`)
      response = false
    }

    return response
  }

  get $element() {
    return $(this.element)
  }

  get $form() {
    return this.$element.closest('form')
  }

  get $submit() {
    return this.$form.find('[type=submit]')
  }

  enableForm() {
    this.formDisabled = false
    this.$submit.prop('disabled', false)
    this.$submit.html(this.enabledFormSubmitContent)
    this.$submit.css('cursor', 'pointer')
  }

  disableForm() {
    if (this.formDisabled) return
    this.formDisabled = true
    this.$submit.prop('disabled', true)
    this.enabledFormSubmitContent = this.$submit.html()
    this.$submit.html(this.$submit.data('disableWith'))
    this.$submit.css('cursor', 'default')
  }

  $progressBar(fileName) {
    return $(`<span class="text-sm">${truncate(fileName, 24)}</span>
      <div class="mb-2">
        <div class="progress-bar bg-blue-400 text-xs leading-none py-1 pl-5 text-black shadow w-full bg-grey-light" style="width:0%">
        0%
        </div>
      </div>`)
  }
}
